import { useEffect } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { LeftArrowIcon } from "@/components/confluence/icons";
import { ConfluenceTemplateMessages } from "@/components/confluence/templates/template-messages";
import { ConfluenceTemplateAnalytics } from "@/components/confluence/templates/util/confluence-templates-analytics";
import { useTemplates } from "@/content/templates/template.provider";
import { useLocalizedHref } from "@/libraries/web-framework";

import {
    ConfluenceTemplateExperimentContextType,
    useConfluenceTemplateExperimentContext
} from "../sections/shared-header-footer-container.tsx";

import { TemplateCategorySection } from "./template-category-section.tsx";
import { TemplateDetailsBody } from "./template-details-body.tsx";
import { TemplateHeaderSection } from "./template-header-section.tsx";
import TemplateKeyFeaturesSection from "./template-key-features-section.tsx";

const detailPageStyles = css({
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    alignItems: "center",
    paddingInlineStart: "16px",
    paddingInlineEnd: "16px"
});

const detailPageContentStyles = css({
    "display": "flex",
    "gap": "31px",
    "justifyContent": "center",
    "maxWidth": "100%",
    "@media(max-width: 768px)": {
        flexDirection: "column"
    }
});

const backLinkStyles = css({
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#6B778C",
    fontSize: "16px",
    fontWeight: "500",
    letterSpacing: "-0.20000000298023224px",
    maxWidth: "1166px",
    padding: "56px 0px 56px 0px",
    width: "100%"
});

const leftSectionStyles = css({
    "display": "flex",
    "flexDirection": "column",
    "width": "368px",
    "paddingBlockStart": "68px",
    "marginBlockStart": "-68px",
    "height": "100%",
    "insetBlockStart": 0,
    "@media(max-width: 768px)": {
        height: "auto",
        paddingInlineStart: "16px",
        paddingInlineEnd: "16px",
        position: "relative",
        width: "100%"
    },
    "@media(min-width: 769px)": {
        position: "sticky"
    }
});

const rightSectionStyles = css({
    "display": "flex",
    "flexDirection": "column",
    "paddingTop": "136px",
    "width": "767px",
    "@media(max-width: 768px)": {
        paddingInlineStart: "16px",
        paddingInlineEnd: "16px",
        paddingBlockStart: "0",
        width: "100%"
    }
});

export const DetailPageContent = () => {
    const templates = useTemplates();
    const template = templates[0];
    const { setTemplate } = useConfluenceTemplateExperimentContext();

    const intl = useIntl();
    const templatesLink = useLocalizedHref("/software/confluence/templates");

    const linkText = intl.formatMessage({
        id: "ConfluenceTemplateMessages.backLink.cfind.1508",
        defaultMessage: "Explore all Confluence templates"
    });

    const backAriaLabel =
        ConfluenceTemplateMessages.searchBackAriaLabel.defaultMessage;

    useEffect(() => {
        if (template) {
            setTemplate({
                templateNameExternal: template?.name,
                pluginModuleKey: template?.pluginModuleKey
            } as ConfluenceTemplateExperimentContextType);
        }
    }, [template, setTemplate]);

    if (!template) {
        return null;
    }

    return (
        <div data-testid="confluence-template-details-page">
            <div css={detailPageStyles}>
                <div css={detailPageContentStyles}>
                    <div css={leftSectionStyles}>
                        <div css={backLinkStyles}>
                            <a
                                href={templatesLink}
                                aria-label={backAriaLabel}
                                {...ConfluenceTemplateAnalytics.searchBackButton()}
                            >
                                <LeftArrowIcon size="13" />
                                {linkText}
                            </a>
                        </div>
                        <TemplateHeaderSection template={template} />
                        <TemplateKeyFeaturesSection template={template} />
                        <TemplateCategorySection template={template} />
                    </div>
                    <div
                        css={rightSectionStyles}
                        data-testid="TemplateDetailsBody-top"
                    >
                        <TemplateDetailsBody template={template} />
                    </div>
                </div>
            </div>
        </div>
    );
};
